import { useState, type FC } from 'react'

import { Select, Toggle } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { Container } from '@skytech/ui/Container'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { Translation } from '@/app/translations/Translation'
import { ADMIN_ROLES } from '@/hooks/access'
import useUser from '@/hooks/user'
import adminQueries from '@/modules/admin/queries'
import adminResources from '@/modules/admin/resources'

export const SettingsPage: FC = () => {
  const navigate = useNavigate()
  const user = useUser()
  const [customer, setCustomer] = useState<{ value: number; label: string } | undefined>()
  const [allowDepartmentHeadOnboardingManagement, setAllowDepartmentHeadOnboardingManagement] =
    useState(true)

  const { data: customers = [] } = useQuery(adminQueries.adminUserCustomersList(), async () => {
    const response = await adminResources.getUserCustomersList()
    if (response.length === 1) {
      setCustomer({ value: response[0].id, label: response[0].name })
    }
    return response
  })

  const customerOptions = customers.map(option => ({
    value: option.id,
    label: String(option.name),
  }))

  const { data } = useQuery(
    adminQueries.adminCustomerSettings(customer?.value),
    async () => {
      if (!customer?.value) throw Error('Company is missing!')
      const response = await adminResources.getCustomerSettings(customer.value)
      setAllowDepartmentHeadOnboardingManagement(
        response?.allowDepartmentHeadOnboardingManagement !== false
      )
      return response
    },
    {
      enabled: !!customer?.value,
    }
  )

  const updateCustomerSettingsMutation = useMutation(adminResources.updateCustomerSettings)
  const canUpdateDepartmentHeadOnboardingManagement =
    !!user?.role && ADMIN_ROLES.includes(user.role)
  const isDepartmentHeadOnboardingManagementChanged =
    canUpdateDepartmentHeadOnboardingManagement &&
    data !== undefined &&
    allowDepartmentHeadOnboardingManagement !== data?.allowDepartmentHeadOnboardingManagement

  return (
    <Container onSwipedLeft={() => navigate('/')}>
      <Container.Header>
        <Translation id="Settings" />
      </Container.Header>

      <Select
        className="mt-4 w-full md:w-2/6"
        capitalizeLabel
        id="customer"
        label={
          <>
            <Translation id="Customer" />*
          </>
        }
        options={customerOptions}
        value={customer?.value}
        marginBottom={16}
        onChange={option => {
          const customerOption = option as { value: number; label: string }
          setCustomer(customerOption)
        }}
      />

      {customer?.value && (
        <Toggle
          id="allowDepartmentHeadOnboardingManagement"
          checked={allowDepartmentHeadOnboardingManagement}
          isDisabled={!canUpdateDepartmentHeadOnboardingManagement}
          marginBottom={16}
          label={<Translation id="Allow department heads to manage onboarding and offboarding" />}
          onChange={e => setAllowDepartmentHeadOnboardingManagement(e.target.checked)}
        />
      )}

      {customer?.value && (
        <Button
          disabled={
            !isDepartmentHeadOnboardingManagementChanged || updateCustomerSettingsMutation.isLoading
          }
          variant="primary"
          isLoading={updateCustomerSettingsMutation.isLoading}
          onClick={e => {
            e.preventDefault()
            if (!isDepartmentHeadOnboardingManagementChanged) {
              return
            }
            updateCustomerSettingsMutation.mutate({
              customerId: customer.value,
              allowDepartmentHeadOnboardingManagement,
            })
          }}
        >
          <Translation id="Save" />
        </Button>
      )}
    </Container>
  )
}
